/**
 * Utility functions for organization-related operations
 */

/**
 * Get the organization logo path based on organization name
 * @param organizationName - The name of the organization
 * @param isDark - Whether to use dark theme styling
 * @returns Object containing SVG and PNG logo paths with fallbacks
 */
export const getOrganizationLogo = (organizationName: string, isDark: boolean = false) => {
  console.log('getOrganizationLogo - organizationName:', organizationName);
  
  if (!organizationName) {
    return getDefaultLogo(isDark);
  }

  // Normalize organization name for file system
  // Remove spaces, special characters, and convert to proper case
  const normalizedName = organizationName
    .trim()
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '') // Remove spaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');

  console.log('getOrganizationLogo - normalizedName:', normalizedName);

  const basePath = `/logos/org/${normalizedName}`;

  const result = {
    svg: `${basePath}.svg`,
    png: `${basePath}.png`,
    fallbackSvg: isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg",
    fallbackPng: isDark ? "/logos/LOGO-MAIN-WHITE.png" : "/logos/LOGO-MAIN-BLK.png",
    alt: `${organizationName} Logo`
  };
  
  console.log('getOrganizationLogo - SVG path:', result.svg);
  return result;
};

/**
 * Get default CGM Sports AI Dashboard logo
 * @param isDark - Whether to use dark theme styling
 * @returns Object containing default logo paths
 */
export const getDefaultLogo = (isDark: boolean = false) => {
  return {
    svg: isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg",
    png: isDark ? "/logos/LOGO-MAIN-WHITE.png" : "/logos/LOGO-MAIN-BLK.png",
    fallbackSvg: isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg",
    fallbackPng: isDark ? "/logos/LOGO-MAIN-WHITE.png" : "/logos/LOGO-MAIN-BLK.png",
    alt: "Wrestling Team"
  };
};

/**
 * React component for displaying organization logos with automatic fallbacks
 * @param organizationName - The name of the organization
 * @param isDark - Whether to use dark theme styling
 * @param className - CSS classes to apply
 * @param size - Size preset (small, medium, large) or custom class
 */
export interface OrganizationLogoProps {
  organizationName?: string;
  isDark?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large' | string;
  onClick?: () => void;
}

/**
 * Get CSS classes for organization logo based on size preset
 */
export const getLogoSizeClass = (size: string = 'medium'): string => {
  const sizeClasses = {
    small: 'h-8 w-auto',
    medium: 'h-12 w-auto',
    large: 'h-16 w-auto'
  };

  return sizeClasses[size as keyof typeof sizeClasses] || size;
};

/**
 * Extract organization name from team data
 * @param team - Team object with organization data
 * @returns Organization name or empty string
 */
export const getOrganizationNameFromTeam = (team: any): string => {
  console.log('getOrganizationNameFromTeam - team name:', team?.name, 'team_name:', team?.hrv_data?.team_name);
  
  if (!team) return '';
  
  // Handle different possible team data structures
  if (team.organization?.name) {
    console.log('getOrganizationNameFromTeam - found organization.name:', team.organization.name);
    return team.organization.name;
  }
  
  // Try to extract from hrv_data.team_name first
  if (team.hrv_data?.team_name) {
    const teamName = team.hrv_data.team_name;
    const match = teamName.match(/^([A-Za-z\s]+)\s+(Wrestling|Football|Soccer|Basketball|Baseball|Track|Swimming|Tennis|Golf|Hockey)/i);
    if (match) {
      const orgName = match[1].trim();
      console.log('getOrganizationNameFromTeam - extracted from team_name:', orgName);
      return orgName;
    }
  }
  
  // Also check team.name directly
  if (team.name) {
    const match = team.name.match(/^([A-Za-z\s]+)\s+(Wrestling|Football|Soccer|Basketball|Baseball|Track|Swimming|Tennis|Golf|Hockey)/i);
    if (match) {
      const orgName = match[1].trim();
      console.log('getOrganizationNameFromTeam - extracted from team.name:', orgName);
      return orgName;
    }
  }
  
  console.log('getOrganizationNameFromTeam - no organization found');
  return '';
};

/**
 * Generate a list of supported organization names for admin interfaces
 * This can be extended to fetch from API or config file
 */
export const getSupportedOrganizations = (): string[] => {
  return [
    'Harvard',
    'Yale',
    'Princeton',
    'Columbia',
    'Brown',
    'Cornell',
    'Dartmouth',
    'Pennsylvania',
    'MIT',
    'Stanford',
    'Berkeley',
    'UCLA',
    'USC',
    // Add more organizations as needed
  ];
}; 